import React, { useState, useEffect } from 'react';
import { Authenticated } from '../../lib/auth/auth-components';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../ui/use-toast';
import { Id } from '../../convex/_generated/dataModel';
import { useAuth } from '../../lib/auth/AuthContext';

type Language = {
  _id: Id<"languages">;
  code: string;
  name: string;
  isDefault: boolean;
  isActive: boolean;
  direction: string;
};

type LanguageFormData = {
  code: string;
  name: string;
  isDefault: boolean;
  isActive: boolean;
  direction: string;
};

const LanguageManagerContent: React.FC = () => {
  const { toast } = useToast();
  const { useAuthenticatedQuery, useAuthenticatedMutation } = useAuth();
  const languages = useAuthenticatedQuery('languages:list', { activeOnly: false }) || [];
  const createLanguage = useAuthenticatedMutation('languages:create');
  const updateLanguage = useAuthenticatedMutation('languages:update');
  const removeLanguage = useAuthenticatedMutation('languages:remove');
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  
  const [formData, setFormData] = useState<LanguageFormData>({
    code: '',
    name: '',
    isDefault: false,
    isActive: true,
    direction: 'ltr',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      isDefault: false,
      isActive: true,
      direction: 'ltr',
    });
  };

  const handleAddLanguage = async () => {
    try {
      await createLanguage({
        code: formData.code,
        name: formData.name,
        isDefault: formData.isDefault,
        isActive: formData.isActive,
        direction: formData.direction,
      });
      toast({
        title: 'Language added',
        description: `The language ${formData.name} has been added successfully.`,
      });
      resetForm();
      setIsAddDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add language',
        variant: 'destructive',
      });
    }
  };

  const handleEditLanguage = (language: Language) => {
    setSelectedLanguage(language);
    setFormData({
      code: language.code,
      name: language.name,
      isDefault: language.isDefault,
      isActive: language.isActive,
      direction: language.direction,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateLanguage = async () => {
    if (!selectedLanguage) return;
    
    try {
      await updateLanguage({
        languageId: selectedLanguage._id,
        name: formData.name,
        isDefault: formData.isDefault,
        isActive: formData.isActive,
        direction: formData.direction,
      });
      toast({
        title: 'Language updated',
        description: `The language ${formData.name} has been updated successfully.`,
      });
      setIsEditDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update language',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteLanguage = (language: Language) => {
    setSelectedLanguage(language);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteLanguage = async () => {
    if (!selectedLanguage) return;
    
    try {
      await removeLanguage({
        languageId: selectedLanguage._id,
      });
      toast({
        title: 'Language deleted',
        description: `The language ${selectedLanguage.name} has been deleted.`,
      });
      setIsDeleteDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete language',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Language Management</h1>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => {
              resetForm();
              setIsAddDialogOpen(true);
            }}>
              Add Language
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Language</DialogTitle>
              <DialogDescription>
                Add a new language to the system. The language code should be unique.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="code" className="text-right">
                  Code
                </Label>
                <Input
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  className="col-span-3"
                  placeholder="en"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="col-span-3"
                  placeholder="English"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="direction" className="text-right">
                  Direction
                </Label>
                <select
                  id="direction"
                  name="direction"
                  value={formData.direction}
                  onChange={handleSelectChange}
                  className="col-span-3 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="ltr">Left to Right (LTR)</option>
                  <option value="rtl">Right to Left (RTL)</option>
                </select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <div className="text-right">Status</div>
                <div className="flex items-center space-x-2 col-span-3">
                  <Checkbox
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) => 
                      handleCheckboxChange('isActive', checked === true)
                    }
                  />
                  <Label htmlFor="isActive">Active</Label>
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <div className="text-right">Default</div>
                <div className="flex items-center space-x-2 col-span-3">
                  <Checkbox
                    id="isDefault"
                    checked={formData.isDefault}
                    onCheckedChange={(checked) => 
                      handleCheckboxChange('isDefault', checked === true)
                    }
                  />
                  <Label htmlFor="isDefault">Make Default Language</Label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddLanguage}>Add Language</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Direction</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Default</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {languages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6 text-gray-500">
                  No languages found. Add your first language to get started.
                </TableCell>
              </TableRow>
            ) : (
              languages.map((language) => (
                <TableRow key={language._id}>
                  <TableCell className="font-medium">{language.code}</TableCell>
                  <TableCell>{language.name}</TableCell>
                  <TableCell>{language.direction === 'rtl' ? 'Right to Left' : 'Left to Right'}</TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        language.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {language.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {language.isDefault ? (
                      <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                        Default
                      </span>
                    ) : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditLanguage(language)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteLanguage(language)}
                        disabled={language.isDefault}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Language</DialogTitle>
            <DialogDescription>
              Update the language details.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-code" className="text-right">
                Code
              </Label>
              <Input
                id="edit-code"
                value={formData.code}
                disabled
                className="col-span-3 bg-gray-50"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-name" className="text-right">
                Name
              </Label>
              <Input
                id="edit-name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="edit-direction" className="text-right">
                Direction
              </Label>
              <select
                id="edit-direction"
                name="direction"
                value={formData.direction}
                onChange={handleSelectChange}
                className="col-span-3 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="ltr">Left to Right (LTR)</option>
                <option value="rtl">Right to Left (RTL)</option>
              </select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <div className="text-right">Status</div>
              <div className="flex items-center space-x-2 col-span-3">
                <Checkbox
                  id="edit-isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => 
                    handleCheckboxChange('isActive', checked === true)
                  }
                />
                <Label htmlFor="edit-isActive">Active</Label>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <div className="text-right">Default</div>
              <div className="flex items-center space-x-2 col-span-3">
                <Checkbox
                  id="edit-isDefault"
                  checked={formData.isDefault}
                  onCheckedChange={(checked) => 
                    handleCheckboxChange('isDefault', checked === true)
                  }
                />
                <Label htmlFor="edit-isDefault">Make Default Language</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateLanguage}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the language{' '}
              <strong>{selectedLanguage?.name}</strong> with code{' '}
              <strong>{selectedLanguage?.code}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteLanguage}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Wrap the component with Authenticated to ensure auth token is passed
export const LanguageManager: React.FC = () => {
  return (
    <Authenticated>
      <LanguageManagerContent />
    </Authenticated>
  );
}; 